/** Capture item video/audio load steps for troubleshooting playback. */

import { copyTextToClipboard } from './formatListClipboard';
import { escapeHtml } from './escapeHtml';
import type { MediaBytesProbe } from './mediaBytesProbe';

export type MediaDebugEntry = {
  id: string;
  timestamp: number;
  kind: 'video' | 'audio';
  phase: string;
  repoPath: string;
  ok: boolean;
  message: string;
  probe?: MediaBytesProbe;
  details?: Record<string, unknown>;
};

const MAX_ENTRIES = 200;
const STORAGE_KEY = 'codenesium.mediaDebug';

const entries: MediaDebugEntry[] = [];
const lastProbeByPath = new Map<string, MediaBytesProbe>();
const listeners = new Set<() => void>();

let modalEl: HTMLElement | null = null;
let listEl: HTMLElement | null = null;
let selectedId: string | null = null;
let nextId = 1;

export type MediaDebugFilter = 'all' | 'playback' | 'thumb';

let listFilter: MediaDebugFilter = 'all';

/** Thumbnail pipeline phases use the thumb- prefix. */
export function isThumbDebugPhase(phase: string): boolean {
  return phase.startsWith('thumb-');
}

export function isPlaybackDebugPhase(phase: string): boolean {
  return !isThumbDebugPhase(phase);
}

export function getMediaDebugFilter(): MediaDebugFilter {
  return listFilter;
}

export function setMediaDebugFilter(filter: MediaDebugFilter): void {
  listFilter = filter;
  renderList();
}

export function logThumbDebug(
  repoPath: string,
  phase: string,
  message: string,
  ok = true,
  details?: Record<string, unknown>
): void {
  if (!isMediaDebugEnabled()) return;
  logMediaDebug({
    kind: 'video',
    phase,
    repoPath,
    ok,
    message,
    ...(details ? { details } : {}),
  });
}

function notify(): void {
  for (const fn of listeners) fn();
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function isMediaDebugEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  if (new URLSearchParams(window.location.search).has('mediaDebug')) return true;
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function setMediaDebugEnabled(on: boolean): void {
  try {
    if (on) localStorage.setItem(STORAGE_KEY, '1');
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
  notify();
}

export function getLastMediaProbe(repoPath: string): MediaBytesProbe | undefined {
  return lastProbeByPath.get(repoPath);
}

export function logMediaDebug(
  partial: Omit<MediaDebugEntry, 'id' | 'timestamp'> & { id?: string; timestamp?: number }
): void {
  const entry: MediaDebugEntry = {
    id: partial.id ?? `m${nextId++}`,
    timestamp: partial.timestamp ?? Date.now(),
    kind: partial.kind,
    phase: partial.phase,
    repoPath: partial.repoPath,
    ok: partial.ok,
    message: partial.message,
    ...(partial.probe ? { probe: partial.probe } : {}),
    ...(partial.details ? { details: partial.details } : {}),
  };
  entries.unshift(entry);
  if (entries.length > MAX_ENTRIES) entries.length = MAX_ENTRIES;
  if (partial.probe) lastProbeByPath.set(partial.repoPath, partial.probe);
  notify();
}

export function clearMediaDebugLog(): void {
  entries.length = 0;
  lastProbeByPath.clear();
  selectedId = null;
  notify();
}

export function getMediaDebugEntries(): readonly MediaDebugEntry[] {
  return entries;
}

function formatTime(ts: number): string {
  return new Date(ts).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    fractionalSecondDigits: 3,
  });
}

function formatEntryForClipboard(entry: MediaDebugEntry): string {
  return JSON.stringify(entry, null, 2);
}

function renderProbe(probe: MediaBytesProbe): string {
  return `<pre class="media-debug-pre">${escapeHtml(JSON.stringify(probe, null, 2))}</pre>`;
}

function renderDetail(entry: MediaDebugEntry): string {
  const rows = [
    `<div class="media-debug-detail-row"><span class="media-debug-detail-label">Path</span><pre class="media-debug-pre">${escapeHtml(entry.repoPath)}</pre></div>`,
    `<div class="media-debug-detail-row"><span class="media-debug-detail-label">Phase</span><pre class="media-debug-pre">${escapeHtml(entry.phase)}</pre></div>`,
    `<div class="media-debug-detail-row"><span class="media-debug-detail-label">Message</span><pre class="media-debug-pre">${escapeHtml(entry.message)}</pre></div>`,
  ];
  if (entry.probe) {
    rows.push(
      `<div class="media-debug-detail-row"><span class="media-debug-detail-label">Byte probe</span>${renderProbe(entry.probe)}</div>`
    );
  }
  if (entry.details) {
    rows.push(
      `<div class="media-debug-detail-row"><span class="media-debug-detail-label">Details</span><pre class="media-debug-pre">${escapeHtml(JSON.stringify(entry.details, null, 2))}</pre></div>`
    );
  }
  return rows.join('');
}

function filteredEntries(): MediaDebugEntry[] {
  if (listFilter === 'thumb') return entries.filter(e => isThumbDebugPhase(e.phase));
  if (listFilter === 'playback') return entries.filter(e => isPlaybackDebugPhase(e.phase));
  return entries;
}

function renderList(): void {
  if (!listEl) return;
  const enabled = isMediaDebugEnabled();
  const visible = filteredEntries();
  if (visible.length === 0) {
    const filterHint =
      listFilter === 'thumb'
        ? 'No thumbnail events yet — attach a video or open a page with clips.'
        : listFilter === 'playback'
          ? 'No playback events yet — tap play on a video tile.'
          : 'No media events yet. Open a page with video (e.g. test) and reload.';
    listEl.innerHTML = `<p class="media-debug-empty">${filterHint}${enabled ? '' : ' Enable capture below first.'}</p>`;
    return;
  }

  listEl.innerHTML = visible
    .map(entry => {
      const open = entry.id === selectedId;
      const statusClass = entry.ok ? 'media-debug-status--ok' : 'media-debug-status--error';
      return `
        <article class="media-debug-entry${open ? ' media-debug-entry--open' : ''}" data-id="${escapeHtml(entry.id)}">
          <div class="media-debug-entry-top">
            <button type="button" class="media-debug-entry-summary">
              <span class="media-debug-entry-time">${escapeHtml(formatTime(entry.timestamp))}</span>
              <span class="media-debug-entry-kind">${escapeHtml(entry.kind)}</span>
              <span class="media-debug-entry-status ${statusClass}">${entry.ok ? 'ok' : 'fail'}</span>
              <span class="media-debug-entry-phase">${escapeHtml(entry.phase)}</span>
              <span class="media-debug-entry-path">${escapeHtml(entry.repoPath.split('/').pop() ?? entry.repoPath)}</span>
            </button>
            <button type="button" class="media-debug-entry-copy btn-ghost-dark btn-sm" aria-label="Copy entry">Copy</button>
          </div>
          ${open ? `<div class="media-debug-entry-detail">${renderDetail(entry)}</div>` : ''}
        </article>
      `;
    })
    .join('');

  listEl.querySelectorAll<HTMLElement>('.media-debug-entry-summary').forEach(btn => {
    btn.addEventListener('click', () => {
      const row = btn.closest('.media-debug-entry');
      const id = row?.getAttribute('data-id') ?? null;
      selectedId = id && selectedId !== id ? id : null;
      renderList();
    });
  });

  listEl.querySelectorAll<HTMLButtonElement>('.media-debug-entry-copy').forEach(btn => {
    btn.addEventListener('click', e => {
      e.stopPropagation();
      const id = btn.closest('.media-debug-entry')?.getAttribute('data-id');
      const entry = id ? entries.find(en => en.id === id) : undefined;
      if (!entry) return;
      const label = btn.textContent;
      void copyTextToClipboard(formatEntryForClipboard(entry)).then(
        () => {
          btn.textContent = 'Copied';
          window.setTimeout(() => {
            btn.textContent = label ?? 'Copy';
          }, 1500);
        },
        () => {
          btn.textContent = 'Failed';
          window.setTimeout(() => {
            btn.textContent = label ?? 'Copy';
          }, 1500);
        }
      );
    });
  });
}

function closeModal(): void {
  if (!modalEl) return;
  modalEl.classList.add('hidden');
  document.body.style.overflow = '';
}

function ensureModal(): HTMLElement {
  if (modalEl) return modalEl;

  const modal = document.createElement('div');
  modal.id = 'media-debug-modal';
  modal.className = 'media-debug-modal hidden';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.setAttribute('aria-label', 'Media debug log');
  modal.innerHTML = `
    <div class="media-debug-modal-backdrop" aria-hidden="true"></div>
    <div class="media-debug-modal-sheet">
      <header class="media-debug-modal-header">
        <h2 class="media-debug-modal-title">Media debug log</h2>
        <div class="media-debug-modal-actions">
          <label class="media-debug-toggle">
            <input type="checkbox" id="media-debug-enabled" />
            Capture on
          </label>
          <button type="button" class="btn-ghost-dark btn-sm" id="media-debug-copy-all">Copy all</button>
          <button type="button" class="btn-ghost-dark btn-sm" id="media-debug-clear">Clear</button>
          <button type="button" class="btn-primary btn-sm" id="media-debug-close">Close</button>
        </div>
      </header>
      <p class="media-debug-modal-hint">Video/audio playback and thumbnail extraction. Add ?mediaDebug=1 to auto-enable capture. Thumbnails: thumb-* phases.</p>
      <div class="media-debug-filters" role="tablist" aria-label="Media log filter">
        <button type="button" class="btn-ghost-dark btn-sm media-debug-filter media-debug-filter--active" data-filter="all">All</button>
        <button type="button" class="btn-ghost-dark btn-sm media-debug-filter" data-filter="playback">Playback</button>
        <button type="button" class="btn-ghost-dark btn-sm media-debug-filter" data-filter="thumb">Thumbnails</button>
      </div>
      <div class="media-debug-list" id="media-debug-list"></div>
    </div>
  `;

  listEl = modal.querySelector('#media-debug-list');
  const enabledInput = modal.querySelector<HTMLInputElement>('#media-debug-enabled')!;
  enabledInput.checked = isMediaDebugEnabled();
  enabledInput.addEventListener('change', () => {
    setMediaDebugEnabled(enabledInput.checked);
    renderList();
  });

  modal.querySelector('.media-debug-modal-backdrop')!.addEventListener('click', closeModal);
  modal.querySelector('#media-debug-close')!.addEventListener('click', closeModal);
  modal.querySelector('#media-debug-clear')!.addEventListener('click', () => {
    clearMediaDebugLog();
    renderList();
  });
  modal.querySelector('#media-debug-copy-all')!.addEventListener('click', () => {
    void copyTextToClipboard(JSON.stringify(filteredEntries(), null, 2));
  });

  modal.querySelectorAll<HTMLButtonElement>('.media-debug-filter').forEach(btn => {
    btn.addEventListener('click', () => {
      const filter = btn.dataset['filter'] as MediaDebugFilter | undefined;
      if (!filter) return;
      setMediaDebugFilter(filter);
      modal.querySelectorAll('.media-debug-filter').forEach(b => {
        b.classList.toggle('media-debug-filter--active', b === btn);
      });
    });
  });

  document.body.appendChild(modal);
  modalEl = modal;
  return modal;
}

export function mountMediaDebugModal(): void {
  ensureModal();
  subscribe(() => {
    if (!modalEl || modalEl.classList.contains('hidden')) return;
    const enabledInput = modalEl.querySelector<HTMLInputElement>('#media-debug-enabled');
    if (enabledInput) enabledInput.checked = isMediaDebugEnabled();
    renderList();
  });
}

export function openMediaDebugModal(): void {
  const modal = ensureModal();
  const enabledInput = modal.querySelector<HTMLInputElement>('#media-debug-enabled');
  if (enabledInput) enabledInput.checked = isMediaDebugEnabled();
  renderList();
  modal.classList.remove('hidden');
  document.body.style.overflow = 'hidden';
}

/** One-line summary for inline tile debug strip. */
export function formatMediaProbeSummary(probe: MediaBytesProbe): string {
  if (probe.isLfsPointer) {
    return `LFS pointer ${probe.byteLength}B → oid ${probe.lfsOid ?? '?'} size ${probe.lfsSize ?? '?'}`;
  }
  const parts = [`${probe.byteLength}B`, probe.suggestedMime];
  if (probe.isMp4Ftyp) parts.push('ftyp');
  if (probe.mp4Codecs?.length) parts.push(probe.mp4Codecs.join('+'));
  if (probe.mp4FastStart === false) parts.push('slow-start');
  if (probe.isMp3) parts.push('mp3');
  if (probe.warning) parts.push(probe.warning);
  if (probe.issue) parts.push(probe.issue);
  return parts.join(' · ');
}
